/**
 * TemplateInfoModal — guia simples para o usuário entender como funcionam
 * variáveis em templates WhatsApp e regras essenciais da Meta.
 *
 * Reusa os estilos do UserGuideModal (user-guide-modal.css + UserGuideModal.css)
 * para manter consistência visual com o guia do fluxo.
 */

import React, { useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import {
  IconBook2,
  IconChevronDown,
  IconChevronRight,
  IconBulb,
  IconAlertTriangle,
  IconCircleCheck,
} from '@tabler/icons-react';
import '../../../../../components/UserGuideModal.css';

// ===============================================
// SECTION DATA
// ===============================================

const SECTIONS = [
  {
    id: 'what-are',
    title: '1. O que são variáveis?',
    content: (
      <>
        <p>
          Templates do WhatsApp são <strong>mensagens pré-aprovadas pela Meta</strong> com
          espaços que mudam a cada envio. Esses espaços são as <strong>variáveis</strong>:
          escritas como <code>{'{{1}}'}</code>, <code>{'{{2}}'}</code>, <code>{'{{3}}'}</code>...
        </p>
        <p>
          Você cria o template <strong>uma vez</strong> com os espaços, e depois usa para
          milhões de mensagens diferentes — só trocando o que vai dentro dos espaços.
        </p>

        <h4>Exemplo</h4>
        <p>Template criado com variáveis:</p>
        <div className="guide-code">
          Olá {'{{1}}'}, seu pedido {'{{2}}'} foi confirmado!
        </div>

        <p>Na hora de enviar para clientes diferentes:</p>
        <table className="guide-table">
          <thead>
            <tr><th>Cliente</th><th>O que ele recebe</th></tr>
          </thead>
          <tbody>
            <tr>
              <td>João (variável 1) <br />Pedido #1234 (variável 2)</td>
              <td>Olá <strong>João</strong>, seu pedido <strong>#1234</strong> foi confirmado!</td>
            </tr>
            <tr>
              <td>Maria (variável 1) <br />Pedido #5678 (variável 2)</td>
              <td>Olá <strong>Maria</strong>, seu pedido <strong>#5678</strong> foi confirmado!</td>
            </tr>
          </tbody>
        </table>

        <div className="guide-callout guide-callout--info">
          <IconBulb size={16} />
          <div>
            <strong>Por que existe?</strong> A Meta proíbe enviar mensagens livres
            para clientes fora da conversa de 24h. Templates com variáveis são a
            forma oficial de mandar mensagens personalizadas em escala.
          </div>
        </div>
      </>
    ),
  },
  {
    id: 'how-to-use',
    title: '2. Como usar no formulário',
    content: (
      <>
        <p>No campo <strong>Corpo</strong> do template, escreva o texto e coloque a variável onde quiser:</p>
        <div className="guide-code">
          Olá {'{{1}}'}, sua compra de {'{{2}}'} foi aprovada
        </div>

        <p>Quando você digita <code>{'{{1}}'}</code>, automaticamente aparece um campo embaixo pedindo um <strong>valor de exemplo</strong>:</p>

        <table className="guide-table guide-table--example">
          <thead>
            <tr><th>Variável</th><th>Valor de exemplo</th></tr>
          </thead>
          <tbody>
            <tr><td><code>{'{{1}}'}</code></td><td>João</td></tr>
            <tr><td><code>{'{{2}}'}</code></td><td>R$ 250,00</td></tr>
          </tbody>
        </table>

        <div className="guide-callout guide-callout--warning">
          <IconAlertTriangle size={16} />
          <div>
            <strong>Os valores de exemplo são obrigatórios.</strong> Não são os
            valores que o cliente final vê — são apenas para a Meta entender
            o tipo de informação que vai ali. <strong>Use exemplos realistas</strong>:
            "João" para nome, "R$ 250,00" para preço, "#1234" para pedido.
          </div>
        </div>
      </>
    ),
  },
  {
    id: 'numbering',
    title: '3. Numeração das variáveis',
    content: (
      <>
        <p>As variáveis são <strong>numeradas em ordem</strong>, começando em <code>{'{{1}}'}</code>.</p>

        <table className="guide-table">
          <thead>
            <tr><th>Texto</th><th>Status</th></tr>
          </thead>
          <tbody>
            <tr>
              <td>Olá {'{{1}}'}, seu saldo é {'{{2}}'}</td>
              <td><span className="guide-tag guide-tag--green">Correto</span></td>
            </tr>
            <tr>
              <td>Olá {'{{2}}'}, seu saldo é {'{{3}}'}</td>
              <td><span className="guide-tag guide-tag--red">Errado</span> — pulou o {'{{1}}'}</td>
            </tr>
            <tr>
              <td>Olá {'{{0}}'}, seu saldo é {'{{1}}'}</td>
              <td><span className="guide-tag guide-tag--red">Errado</span> — começa em {'{{1}}'}, nunca em {'{{0}}'}</td>
            </tr>
          </tbody>
        </table>

        <div className="guide-callout guide-callout--info">
          <IconBulb size={16} />
          <div>
            Você pode <strong>repetir a mesma variável</strong> várias vezes. Por exemplo:
            "Olá {'{{1}}'}! Vamos confirmar seu cadastro, {'{{1}}'}." — usa o mesmo nome duas vezes.
          </div>
        </div>
      </>
    ),
  },
  {
    id: 'where',
    title: '4. Onde posso usar variáveis?',
    content: (
      <>
        <p>Não é em todo lugar. Cada parte do template tem regras diferentes:</p>

        <table className="guide-table">
          <thead>
            <tr><th>Parte</th><th>Aceita variáveis?</th><th>Observação</th></tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Cabeçalho (texto)</strong></td>
              <td><span className="guide-tag guide-tag--green">Sim</span></td>
              <td>Geralmente apenas <code>{'{{1}}'}</code></td>
            </tr>
            <tr>
              <td><strong>Cabeçalho (mídia)</strong></td>
              <td><span className="guide-tag guide-tag--slate">N/A</span></td>
              <td>A própria mídia varia por envio</td>
            </tr>
            <tr>
              <td><strong>Corpo</strong></td>
              <td><span className="guide-tag guide-tag--green">Sim</span></td>
              <td>Quantas precisar (com bom senso)</td>
            </tr>
            <tr>
              <td><strong>Rodapé</strong></td>
              <td><span className="guide-tag guide-tag--red">Não</span></td>
              <td>Texto fixo apenas</td>
            </tr>
            <tr>
              <td><strong>Botão URL</strong></td>
              <td><span className="guide-tag guide-tag--green">Sim</span></td>
              <td>Apenas no fim da URL: <code>https://site.com/pedido/{'{{1}}'}</code></td>
            </tr>
            <tr>
              <td><strong>Botão "Resposta rápida"</strong></td>
              <td><span className="guide-tag guide-tag--red">Não</span></td>
              <td>Texto do botão é fixo</td>
            </tr>
          </tbody>
        </table>
      </>
    ),
  },
  {
    id: 'rules',
    title: '5. Regras importantes da Meta',
    content: (
      <>
        <p>A Meta revisa cada template antes de aprovar. Para passar na revisão:</p>

        <h4>Sempre faça</h4>
        <ul>
          <li><strong>Comece e termine com texto</strong>, não com variável.
            <br />❌ <code>{'{{1}}'} aprovado</code> · ✅ <code>Olá {'{{1}}'}, sua compra foi aprovada!</code></li>
          <li><strong>Mantenha o sentido completo</strong> — o texto fixo deve fazer sentido sozinho, sem as variáveis</li>
          <li><strong>Use exemplos realistas</strong> nos valores de exemplo (João, R$ 250, #1234)</li>
        </ul>

        <h4>Nunca faça</h4>
        <ul>
          <li><strong>Variável solta:</strong> mensagem que é só uma variável (<code>{'{{1}}'}</code>)</li>
          <li><strong>Mais de 50% de variáveis:</strong> tipo "Olá {'{{1}}'} {'{{2}}'} {'{{3}}'}"</li>
          <li><strong>Variáveis fora de ordem:</strong> usar <code>{'{{2}}'}</code> sem ter <code>{'{{1}}'}</code></li>
          <li><strong>Exemplos genéricos:</strong> "teste", "X", "abc", "asdf" — Meta rejeita</li>
        </ul>

        <div className="guide-callout guide-callout--danger">
          <IconAlertTriangle size={16} />
          <div>
            <strong>Templates rejeitados não podem ser editados</strong>, só excluídos
            e recriados. Por isso vale a pena revisar com calma antes de enviar.
          </div>
        </div>
      </>
    ),
  },
  {
    id: 'good-examples',
    title: '6. Exemplos bons (para inspirar)',
    content: (
      <>
        <h4>Confirmação de pedido</h4>
        <div className="guide-code">
          Olá {'{{1}}'}! Seu pedido {'{{2}}'} foi confirmado e será enviado em até {'{{3}}'} dias úteis. Acompanhe pelo link no botão abaixo.
        </div>
        <p style={{ fontSize: 13, color: '#6b7280', margin: '4px 0 16px' }}>
          Exemplos: {'{{1}}'}=João, {'{{2}}'}=#A1234, {'{{3}}'}=3
        </p>

        <h4>Lembrete de agendamento</h4>
        <div className="guide-code">
          Oi {'{{1}}'}, lembrando da sua consulta amanhã, dia {'{{2}}'} às {'{{3}}'}. Em caso de cancelamento, responda esta mensagem.
        </div>
        <p style={{ fontSize: 13, color: '#6b7280', margin: '4px 0 16px' }}>
          Exemplos: {'{{1}}'}=Maria, {'{{2}}'}=15/05, {'{{3}}'}=14h00
        </p>

        <h4>Notificação de boleto</h4>
        <div className="guide-code">
          {'{{1}}'}, seu boleto no valor de {'{{2}}'} vence em {'{{3}}'}. Toque no botão para visualizar e pagar.
        </div>
        <p style={{ fontSize: 13, color: '#6b7280', margin: '4px 0 16px' }}>
          Exemplos: {'{{1}}'}=Ana Souza, {'{{2}}'}=R$ 189,90, {'{{3}}'}=20/05/2025
        </p>

        <div className="guide-callout guide-callout--success">
          <IconCircleCheck size={16} />
          <div>
            <strong>Padrão que sempre funciona:</strong> nome do cliente em {'{{1}}'},
            dados específicos do contexto (pedido, valor, data) nas variáveis seguintes,
            sempre cercados de texto fixo que dá contexto.
          </div>
        </div>
      </>
    ),
  },
];

// ===============================================
// ACCORDION SECTION
// ===============================================

const GuideSection = ({ section, isOpen, onToggle }) => (
  <div className={`guide-section ${isOpen ? 'guide-section--open' : ''}`}>
    <button className="guide-section__header" onClick={onToggle}>
      <span className="guide-section__title">{section.title}</span>
      {isOpen ? <IconChevronDown size={18} /> : <IconChevronRight size={18} />}
    </button>
    {isOpen && (
      <div className="guide-section__content">{section.content}</div>
    )}
  </div>
);

// ===============================================
// MAIN MODAL
// ===============================================

const TemplateInfoModal = ({ show, onHide }) => {
  const [openSections, setOpenSections] = useState(['what-are']);

  const toggleSection = (id) => {
    setOpenSections((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id],
    );
  };

  const expandAll = () => setOpenSections(SECTIONS.map((s) => s.id));
  const collapseAll = () => setOpenSections([]);

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="lg"
      className="user-guide-modal"
      scrollable
    >
      <Modal.Header closeButton className="user-guide-modal__header">
        <Modal.Title className="user-guide-modal__title">
          <div className="user-guide-modal__title-icon">
            <IconBook2 size={22} />
          </div>
          Como funcionam variáveis em templates
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="user-guide-modal__body">
        <p className="user-guide-modal__intro">
          Variáveis (<code>{'{{1}}'}</code>, <code>{'{{2}}'}</code>) deixam o
          mesmo template servir para milhões de envios diferentes. Aqui você
          aprende como usá-las e o que a Meta exige para aprovar.
          Clique em cada seção para expandir.
        </p>

        <div className="user-guide-modal__controls">
          <button className="user-guide-modal__control-btn" onClick={expandAll}>
            Expandir tudo
          </button>
          <button className="user-guide-modal__control-btn" onClick={collapseAll}>
            Recolher tudo
          </button>
        </div>

        <div className="guide-sections">
          {SECTIONS.map((section) => (
            <GuideSection
              key={section.id}
              section={section}
              isOpen={openSections.includes(section.id)}
              onToggle={() => toggleSection(section.id)}
            />
          ))}
        </div>
      </Modal.Body>
      <Modal.Footer className="user-guide-modal__footer">
        <Button variant="primary" size="sm" onClick={onHide}>
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default TemplateInfoModal;
